import { FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { SidePanel } from '@carbon/ibm-products';
import { InlineNotification, TextInput } from '@carbon/react';
import type { InterestDetailFields } from './types-interest';
import {
  createInterestAtom,
  editInterestAtom,
  interestDetailUrl,
  interestOverviewUrl,
} from './atoms/interests';
import {
  INTEREST_FIELDS,
  buildInterestRequestBody,
  seedInterestValue,
} from './interest-utils';
import styles from '../../afe-catalog/structuring/identification.module.scss';

// Edit / create panel for a DO interest line (DOLI). Mirrors the AFE
// `identification-edit-panel.tsx` SidePanel form pattern, but every field submits
// FLAT under `interest` (snake_case — the controller's `interest_params` permit
// list). There is NO extension_data block. Blank strings normalize to null.
//
// Request bodies stay snake_case; responses are read camelCase (KEEP). Edit mode
// submits ONLY the fields the user actually changed (the backend leaves omitted
// columns untouched); create mode submits the full field set. The field mapping
// and payload building live in interest-utils (buildInterestRequestBody) so the
// payload contract is unit-testable without rendering this Carbon SidePanel.

interface Props {
  open: boolean;
  /** 'edit' patches an existing line; 'create' adds a new one. */
  mode: 'edit' | 'create';
  documentId: number | string;
  /** The DOLI SF Id (edit mode only; a string). */
  lineItemId?: string;
  /** Seed values for edit mode (the line's current field set). */
  detail?: InterestDetailFields;
  onClose: () => void;
  onSaved: () => void;
}

export const InterestEditPanel: FC<Props> = ({
  open,
  mode,
  documentId,
  lineItemId,
  detail,
  onClose,
  onSaved,
}) => {
  const editInterest = useSetAtom(editInterestAtom);
  const createInterest = useSetAtom(createInterestAtom);

  // Flat working copy keyed by def.key (string form for every input).
  const [values, setValues] = useState<Record<string, string>>({});
  // The seeded baseline — lets edit mode submit ONLY the fields the user changed
  // (buildInterestRequestBody diffs against it). Sending every field re-writes
  // untouched columns (e.g. needlessly re-resolving the owner→entity citation on
  // an unchanged owner); the backend leaves omitted fields untouched.
  const [seeded, setSeeded] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      return;
    }
    const next: Record<string, string> = {};
    INTEREST_FIELDS.forEach((def) => {
      next[def.key] = mode === 'edit' ? seedInterestValue(def, detail) : '';
    });
    setValues(next);
    setSeeded(next);
  }, [open, mode, detail]);

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    const result = buildInterestRequestBody(mode, values, seeded);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    const { interest } = result;

    // Edit with no changes: nothing to persist — close without a no-op PATCH
    // (which would otherwise fire an InterestLineEdited event for an unchanged
    // line and flip its status to `edited`).
    if (mode === 'edit' && Object.keys(interest).length === 0) {
      onSaved();
      return;
    }

    if (mode === 'edit') {
      // Guard before setPending so a missing id doesn't leave the panel spinning;
      // it also narrows lineItemId to a string for interestDetailUrl.
      if (!lineItemId) return;
      setPending(true);
      setError(null);
      editInterest({
        url: interestDetailUrl(documentId, lineItemId),
        method: 'PATCH',
        data: { interest },
        onSuccess: () => {
          setPending(false);
          onSaved();
        },
        onError: (m) => {
          setPending(false);
          setError(m ?? 'Failed to save interest.');
        },
      });
      return;
    }

    setPending(true);
    setError(null);
    createInterest({
      url: interestOverviewUrl(documentId),
      method: 'POST',
      data: { interest },
      onSuccess: () => {
        setPending(false);
        onSaved();
      },
      onError: (m) => {
        setPending(false);
        setError(m ?? 'Failed to add interest.');
      },
    });
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={mode === 'edit' ? 'Edit Interest' : 'Add Interest'}
      subtitle="Update any field below and click Save to apply your changes."
      slideIn
      selectorPageContent=".do-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save',
          disabled: pending,
          onClick: handleSave,
        },
      ]}
    >
      <div className={`${styles.panelForm} ${styles.panelLightFields}`}>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        <div className={styles.panelColumn}>
          {INTEREST_FIELDS.map((def) => {
            // Property identity is read-only in edit mode (the update path does
            // not accept it) — shown for context, but not editable.
            const readOnly = mode === 'edit' && !!def.createOnly;
            return (
              <TextInput
                key={def.key}
                id={`do-interest-${def.key}`}
                light
                labelText={def.label}
                size="sm"
                readOnly={readOnly}
                value={values[def.key] ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(def.key, e.target.value)
                }
              />
            );
          })}
        </div>
      </div>
    </SidePanel>
  );
};
